// @givmo/disclosure — the resolve client (Combined Plan 03 §7a).
//
// One resolve path for every surface, generalizing the donate app's bespoke `GIFT_*` resolver:
// `GET /disclaimers/resolve?surface=…` through the caller's configured `@givmo/api-client` client
// (per-request isolated on SSR, the shared App-Check'd client in the browser — this package never
// constructs clients).
//
// ETag / `content_hash` / 304 caching (§7a): the server sets `ETag: <content_hash>` (bare hash —
// the content_hash IS the ETag) and answers 304 to a matching `If-None-Match`. With a cache
// supplied, a hit sends `If-None-Match: <hash>` verbatim; 304 → the cached entry, 200 → fresh
// body + hash stored. The cache key is the REQUEST identity (`surface|target|jurisdiction|locale`,
// plus the rail and payer when sent) — the server derives `applies_to_state` per target, rail and
// payer, so two of them must never share an entry (the iOS `DisclaimerViewModel` per-target
// cache-key rule).
//
// NEVER throws. 404 (dark-DRAFT — EXPECTED) / any error / an empty body degrades to the caller's
// compiled-in floor, or to `null` for a floor-less surface (render NOTHING, echo nothing).

import { sdk, type Client, type DisclaimersResolveDisclaimerData } from '@givmo/api-client';
import {
  floorDisclosure,
  splitPlaintext,
  type DisclosureFloor,
  type DisclosureFunding,
  type DisclosurePayer,
  type DisclosureSurface,
  type DisclosureTarget,
  type ResolvedDisclosure,
} from './disclosure';

/**
 * The resolve query, widened by `payer` ahead of a snapshot regen (CAM-X3 — the backend's
 * additive query key). A typed const, so the generated client serializes it onto the wire
 * (`agent_docs/api-client.md` § "Additive request fields ahead of a regen"); a backend that
 * predates the key ignores it.
 */
type ResolveQuery = DisclaimersResolveDisclaimerData['query'] & { payer?: DisclosurePayer };

/** One cached resolve: the disclosure + the hash to revalidate with (`If-None-Match`). */
interface DisclosureCacheEntry {
  readonly etag: string;
  readonly disclosure: ResolvedDisclosure;
}

/**
 * An in-memory ETag cache for {@link resolveDisclosure}. Keyed by request identity; entries are
 * revalidated with a conditional GET on every resolve (a 304 costs headers, not body). Share one
 * per execution context (module-level in the browser; a module-level instance is safe on the SSR
 * server too — entries are keyed by full request identity and always revalidated).
 */
export class DisclosureCache {
  private readonly entries = new Map<string, DisclosureCacheEntry>();

  get(key: string): DisclosureCacheEntry | undefined {
    return this.entries.get(key);
  }

  set(key: string, entry: DisclosureCacheEntry): void {
    this.entries.set(key, entry);
  }

  clear(): void {
    this.entries.clear();
  }
}

/** Construct a fresh {@link DisclosureCache}. */
export function createDisclosureCache(): DisclosureCache {
  return new DisclosureCache();
}

export interface ResolveDisclosureOptions {
  /** The configured api-client to resolve through (SSR per-request, or the browser client). */
  client: Client;
  /** The registry surface key (e.g. `DONATE_CONFIRMATION`). */
  surface: DisclosureSurface;
  /** The donation target for a target-derived resolve, or absent for a bare-surface resolve. */
  target?: DisclosureTarget | null;
  /** The funding rail for a rail-keyed resolve, or absent for today's rail-agnostic body. */
  funding?: DisclosureFunding | null;
  /** Who pays (CAM-X3), or absent for the person's body. Rides with `funding` on a one-time resolve. */
  payer?: DisclosurePayer | null;
  /** Optional jurisdiction key (server defaults to US). */
  jurisdiction?: string;
  /** Optional locale key (server defaults to en). */
  locale?: string;
  /**
   * The compiled-in floor for a STATICALLY-FLOORED surface. Absent/`null` = floor-less: resolve
   * failure (404/error/empty) returns `null` and the surface renders nothing.
   */
  floor?: DisclosureFloor | null;
  /** The ETag cache (opt-in). Absent = unconditional GET, no caching. */
  cache?: DisclosureCache | null;
}

/**
 * The request-identity cache key: `surface|targetKind:targetId|jurisdiction|locale`, plus
 * `|funding:<rail>` / `|payer:<payer>` only when the request carries them — so every key a
 * rail-agnostic caller builds is unchanged, and two rails or payers never share an entry (each is
 * its own body).
 */
export function disclosureCacheKey(
  options: Pick<
    ResolveDisclosureOptions,
    'surface' | 'target' | 'funding' | 'payer' | 'jurisdiction' | 'locale'
  >,
): string {
  const target = options.target ? `${options.target.kind}:${options.target.id}` : '';
  return [
    options.surface,
    target,
    options.jurisdiction ?? '',
    options.locale ?? '',
    ...(options.funding ? [`funding:${options.funding}`] : []),
    ...(options.payer ? [`payer:${options.payer}`] : []),
  ].join('|');
}

/** Map the target onto the resolve endpoint's mutually-exclusive `*_id` query params. */
function targetQuery(
  target: DisclosureTarget | null | undefined,
): Partial<Record<'fundraiser_id' | 'charity_id' | 'etf_id' | 'collection_id', string>> {
  if (!target) return {};
  switch (target.kind) {
    case 'fundraiser':
      return { fundraiser_id: target.id };
    case 'charity':
      return { charity_id: target.id };
    case 'etf':
      return { etf_id: target.id };
    case 'collection':
      return { collection_id: target.id };
  }
}

/**
 * Resolve the disclosure for a surface (optionally target-derived). Returns the live registry row
 * on 200, the cached copy on 304, the compiled-in floor on 404/any error/empty body when one is
 * supplied, else `null`. NEVER throws — a fetch failure is graceful degradation, not a 500.
 */
export async function resolveDisclosure(
  options: ResolveDisclosureOptions,
): Promise<ResolvedDisclosure | null> {
  const fallback = options.floor ? floorDisclosure(options.floor) : null;
  const cacheKey = disclosureCacheKey(options);
  const cached = options.cache?.get(cacheKey);

  // Built as a typed const, not inline: `payer` is not in the generated query type yet, and an
  // inline literal would trip the excess-property check (see `ResolveQuery`).
  const query: ResolveQuery = {
    surface: options.surface,
    ...targetQuery(options.target),
    ...(options.funding ? { funding: options.funding } : {}),
    ...(options.payer ? { payer: options.payer } : {}),
    ...(options.jurisdiction ? { jurisdiction: options.jurisdiction } : {}),
    ...(options.locale ? { locale: options.locale } : {}),
  };

  try {
    const { data, error, response } = await sdk.disclaimersResolveDisclaimer({
      client: options.client,
      query,
      // Conditional GET: revalidate a cached entry with its hash, verbatim (§7a). The generated
      // client routes a 304 through its error path (a 304 is non-ok to fetch), so it is detected
      // off `response.status` below, before any error handling.
      ...(cached ? { headers: { 'If-None-Match': cached.etag } } : {}),
    });

    // 304 Not Modified: the cached copy is current — keep it. (Only reachable when a cached entry
    // supplied the If-None-Match; a defensive 304 with no cache entry degrades like an error.)
    if (response?.status === 304) {
      return cached ? cached.disclosure : fallback;
    }

    // 404 (DRAFT/dormant — EXPECTED) / any mapped GivmoError / a 2xx with no body → floor or null.
    if (error || !data) return fallback;

    const lines = splitPlaintext(data.content_plaintext);
    // An empty/whitespace-only body is a contract anomaly — degrade rather than render an empty
    // disclosure.
    if (lines.length === 0) return fallback;

    const disclosure: ResolvedDisclosure = {
      lines,
      contentHash: data.content_hash,
      versionId: data.id,
      isFloor: false,
      appliesToState: data.applies_to_state,
    };
    // Cache under the body's content_hash: the hash IS the ETag (bare, unquoted), so the body value
    // stands in even where CORS hides the response header from browser JS.
    options.cache?.set(cacheKey, {
      etag: response?.headers.get('ETag') ?? data.content_hash,
      disclosure,
    });
    return disclosure;
  } catch {
    // Transport/parse error → floor or null. A render must never crash on the disclosure fetch.
    return fallback;
  }
}
